import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import {
  BreadcrumbService,
  CellTemplateContext,
  DatasetTableConfig,
  TruncateDirective,
  UiDatasetTableComponent,
  UiNoDataComponent,
  UiPreloaderComponent,
  UiSearchComponent,
} from '@ui';
import { routerSegments } from '../../consts/routes';
import { features } from '../../consts/text.const';
import { orgWallsFeature } from '../../store/state/org-walls/org-walls.feature';
import { accountsMapFeature } from '../../store/state/accounts-map/accounts-map.feature';
import { OrgWallEntry } from '../../store/state/org-walls/org-walls.models';
import { createOrgWallsTableConfig } from './walls-list-table.util';
import { WallsModels } from '../../models/walls.model';
import { OrgWallsActions } from '../../store/state/org-walls/org-walls.actions';
import { AlertIconsService } from '../../services/alert-icons.service';

@Component({
  selector: 'walls-list',
  standalone: true,
  imports: [
    FormsModule,
    TranslatePipe,
    MatSelectModule,
    MatIconModule,
    MatTooltip,
    UiSearchComponent,
    UiDatasetTableComponent,
    UiPreloaderComponent,
    UiNoDataComponent,
    TruncateDirective,
  ],
  templateUrl: './walls-list.component.html',
  styleUrl: './walls-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WallsListComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly alertIconsService = inject(AlertIconsService);
  private readonly searchQueryChanged$ = new Subject<string>();
  private readonly typeCellTemplate =
    viewChild<TemplateRef<CellTemplateContext<OrgWallEntry>>>('typeCell');

  public readonly noResults = features.walls.noResults;
  public readonly noData = features.walls.noData;

  public readonly walls = this.store.selectSignal(orgWallsFeature.selectAll);
  public readonly loading = this.store.selectSignal(
    orgWallsFeature.selectIsLoading,
  );
  public readonly isLastPage = this.store.selectSignal(
    orgWallsFeature.selectIsLastPage,
  );
  public readonly loaded = this.store.selectSignal(orgWallsFeature.selectLoaded);
  public readonly error = this.store.selectSignal(orgWallsFeature.selectError);
  public readonly accounts = this.store.selectSignal(accountsMapFeature.selectAll);

  public readonly searchQuery = signal<string>('');
  public readonly selectedOrganization = signal<string | null>(null);

  public readonly organizationOptions = computed<
    WallsModels.OrganizationFilterOption[]
  >(() => {
    const organizationMap = new Map<string, string>();

    for (const account of this.accounts()) {
      if (account.organizationId && account.organizationName) {
        organizationMap.set(account.organizationId, account.organizationName);
      }
    }

    return Array.from(organizationMap.entries())
      .map(
        ([id, name]): WallsModels.OrganizationFilterOption => ({
          id,
          name,
        }),
      )
      .sort((left, right) =>
        left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }),
      );
  });

  public readonly hasActiveFilters = computed<boolean>(
    () =>
      this.normalizeSearch(this.searchQuery()).length > 0 ||
      this.selectedOrganization() !== null,
  );
  public readonly selectedOrganizationName = computed<string | null>(() => {
    const selectedOrganizationId = this.selectedOrganization();
    if (!selectedOrganizationId) {
      return null;
    }

    return (
      this.organizationOptions().find(
        (organization) => organization.id === selectedOrganizationId,
      )?.name ?? null
    );
  });

  public readonly tableConfig = computed<DatasetTableConfig<OrgWallEntry> | null>(() => {
    const typeCell = this.typeCellTemplate();
    if (!typeCell) {
      return null;
    }

    return createOrgWallsTableConfig({
      templates: {
        typeCell,
      },
      onRowClick: (wall) => this.openWall(wall),
    });
  });

  public ngOnInit(): void {
    this.searchQueryChanged$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.requestFilteredWalls();
      });

    void this.breadcrumbService.updateBreadcrumb(this.route, this.router, [
      { name: 'HOME.TITLE', route: routerSegments.home },
      { name: 'SIDEBAR.WALLS' },
    ]);
  }

  public onSearchQueryChange(query: string): void {
    this.searchQuery.set(query);
    this.searchQueryChanged$.next(query);
  }

  public onOrganizationFilterChange(organizationId: string | null): void {
    this.selectedOrganization.set(organizationId);
    this.requestFilteredWalls();
  }

  public openWall(wall: OrgWallEntry): void {
    void this.router.navigate([routerSegments.wallView], {
      queryParams: {
        wallId: wall._id,
        accountId: wall.accountId,
        orgId: wall.organizationId,
      },
    });
  }

  public onLoadMore(): void {
    if (this.loading() || this.isLastPage()) {
      return;
    }

    this.store.dispatch(OrgWallsActions.getNextPage());
  }

  private normalizeSearch(value: string): string {
    return value.trim().toLowerCase();
  }

  private requestFilteredWalls(): void {
    const searchQuery = this.normalizeSearch(this.searchQuery());
    const selectedOrganizationId = this.selectedOrganization();

    this.store.dispatch(
      OrgWallsActions.loadOrgWalls({
        query: {
          query: searchQuery || undefined,
          orgIds: selectedOrganizationId ? [selectedOrganizationId] : undefined,
          limit: WallsModels.ORG_WALLS_PAGE_LIMIT,
        },
      }),
    );
  }
}
